/** @jsxImportSource @emotion/react */

'use client';
import { type SxProps, Typography } from '@mui/material';
import type { CSSProperties, ElementType, ReactNode } from 'react';

import { campaignManagerTheme } from '@campaign-manager/theme';

import * as styles from './styles';

export type Size = keyof typeof campaignManagerTheme.fontSize;

export type Color = keyof typeof campaignManagerTheme.color | 'inherit';

export type Font = 'regular' | 'mono';

function isThemeColor(color: string): color is keyof typeof campaignManagerTheme.color {
  return Object.prototype.hasOwnProperty.call(campaignManagerTheme.color, color);
}

interface CommonTextProps {
  component?: ElementType;
  color?: Color | string;
  align?: 'left' | 'center' | 'right';
  weight?: 'regular' | 'medium' | 'bold';
  whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line' | 'break-spaces';
  italic?: boolean;
  style?: CSSProperties;
  sx?: SxProps;
  children: ReactNode;
}

export interface Props extends CommonTextProps {
  size?: Size;
}

export default function Text({
  component,
  size,
  color,
  align,
  weight,
  whiteSpace,
  italic,
  style,
  children,
  sx,
}: Props) {
  return (
    <Typography
      {...{
        css: [
          {
            fontSize: 'inherit',
            fontWeight: 'inherit',
          },
          size && styles.size(size),
          color &&
            styles.color(
              color === 'inherit' || !isThemeColor(color)
                ? color
                : campaignManagerTheme.color[color],
            ),
          align === 'left' && styles.alignLeft,
          align === 'center' && styles.alignCenter,
          align === 'right' && styles.alignRight,
          weight === 'regular' && styles.weightRegular,
          weight === 'medium' && styles.weightMedium,
          weight === 'bold' && styles.weightBold,
          whiteSpace && styles.whiteSpace(whiteSpace),
          italic && styles.italic,
        ],
        component,
        style,
        sx,
        children,
      }}
    />
  );
}
